import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { refreshMyPicksCache } from '../../api/squares/myPicksCache';
import PopUpAdComponent from '../adverts/PopUpAdComponent';
import { useCachedImage } from '../../hooks/useCachedImage';
import { useTenant } from '@/contexts/TenantContext';
import AdSlot from '../ads/AdSlot';
import { generateAdDivId, withContestTargeting } from '@/config/ads.config';
import { useOverLogoPlacement } from '@/hooks/useOverLogoPlacement';
import SquaresModalShell, { SquaresPrimaryButton } from './SquaresModalShell';
import { appendLobbyParams } from '@/utils/lobbyNavigation';

export interface SquareSubmittedModalProps {
  /** Is the modal open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Team 1 name/abbreviation */
  team1Name?: string;
  /** Team 1 jersey image URL */
  team1JerseyUrl?: string;
  /** Team 2 name/abbreviation */
  team2Name?: string;
  /** Team 2 jersey image URL */
  team2JerseyUrl?: string;
  /** Game date string (e.g., "Thu Mar 19") */
  gameDate?: string;
  /** Game time string (e.g., "1:00pm") */
  gameTime?: string;
  /** Sponsor logo URL (e.g., Wrangler SQUARES logo) */
  sponsorLogoUrl?: string;
  /** Promotional image URL for PopUpAdComponent — desktop, and the fallback for every device */
  promoImageUrl?: string;
  /** Optional narrow-screen crop of the promotional image; falls back to `promoImageUrl` */
  promoImageMobileUrl?: string;
  /** Promotional ad click URL */
  promoClickUrl?: string;
  /**
   * Contest id the entry was just made in. Drives `pid`/`s5` key-value targeting on the
   * post-entry and over-logo GAM slots, and — more importantly — pins both exits to this
   * contest rather than whichever one the app-entry redirect would pick.
   */
  contestId?: string | null;
}

/**
 * How long to wait for GAM to answer before treating the post-entry slot as unsold.
 *
 * `slotRenderEnded` normally arrives well inside a second, but it never arrives at
 * all if GPT is blocked, fails to load, or the request is dropped — and the modal
 * would then hold an empty reserved block for as long as the user has it open.
 * Falling back on a timer bounds that: the worst case is the contest promo appearing
 * a beat late, never a blank space.
 */
export const POST_ENTRY_FILL_TIMEOUT_MS = 3000;

/** Whether GAM has answered for the post-entry slot, and how. */
type PostEntryFill = 'pending' | 'filled' | 'empty';

/**
 * Square Submitted Modal
 * Confirmation popup shown after submitting a square entry
 */
export default function SquareSubmittedModal({
  isOpen,
  onClose,
  team1Name = 'DUKE',
  team1JerseyUrl,
  team2Name = 'ARIZ',
  team2JerseyUrl,
  gameDate = '',
  gameTime = '',
  sponsorLogoUrl,
  promoImageUrl,
  promoImageMobileUrl,
  promoClickUrl,
  contestId,
}: SquareSubmittedModalProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const team1Image = useCachedImage(team1JerseyUrl);
  const team2Image = useCachedImage(team2JerseyUrl);
  const { isAdsEnabled, adPlacements, isPlacementEnabled } = useTenant();

  const isLogoAdEnabled =
    isAdsEnabled && adPlacements?.SQUARES_OVER_LOGO && isPlacementEnabled('SQUARES_OVER_LOGO');
  const overLogoPlacement = useOverLogoPlacement(adPlacements?.SQUARES_OVER_LOGO, contestId);

  // The post-entry slot is admin-configured (Admin → Tenant → Ads → Squares → Post Entry),
  // exactly like Pick'em's POST_ENTRY. The ad unit path is derived server-side from the
  // placement's s1/s2/s3 targeting, so nothing about the path is invented here — an
  // unconfigured placement simply yields no adUnitPath and the static promo shows instead.
  const configuredPostEntry = adPlacements?.SQUARES_POST_ENTRY;

  // Memoised deliberately. AdSlot keys its define/destroy effect on the `sizes` and
  // `targeting` *identities*, so a fresh object each render tears the slot down and
  // re-requests it. This modal is an unmemoised child of PlayView, which holds several
  // react-query subscriptions — any refetch-on-focus or cache update while the modal is
  // open would otherwise burn a duplicate impression and flicker the creative.
  //
  // `enabled` is read off the placement rather than via isPlacementEnabled() so the memo
  // deps stay honest — the context rebuilds that helper on every render. Both read the
  // same underlying row. adUnitPath is checked too: TenantContext drops placements that
  // resolved no path, so an "enabled" placement can still be missing from adPlacements.
  const postEntryPlacement = useMemo(() => {
    if (!isAdsEnabled || !configuredPostEntry?.enabled || !configuredPostEntry.adUnitPath) {
      return null;
    }
    return {
      ...configuredPostEntry,
      targeting: withContestTargeting(configuredPostEntry.targeting, contestId),
    };
  }, [isAdsEnabled, configuredPostEntry, contestId]);

  /*
   * GAM keeps first refusal on the space, but only if it actually sells something.
   * An unsold slot used to leave a reserved, permanently blank block where the
   * contest's own submit-view promo would otherwise be — the promo branch below is
   * unreachable on any tenant with the placement enabled, which is all of them.
   */
  const [postEntryFill, setPostEntryFill] = useState<PostEntryFill>('pending');

  // Each open is a fresh ad request, so it gets a fresh verdict. The shell unmounts
  // its children while closed, but this component itself stays mounted.
  useEffect(() => {
    if (isOpen) setPostEntryFill('pending');
  }, [isOpen]);

  const showPostEntryGam = Boolean(postEntryPlacement && adPlacements) && postEntryFill !== 'empty';

  useEffect(() => {
    if (!isOpen || !showPostEntryGam || postEntryFill !== 'pending') return;

    const timer = setTimeout(() => setPostEntryFill('empty'), POST_ENTRY_FILL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isOpen, showPostEntryGam, postEntryFill]);

  const handleClose = async () => {
    onClose();
    await refreshMyPicksCache(queryClient);
    navigate(appendLobbyParams('/my-picks', searchParams));
  };

  /**
   * Always returns to the contest the entry was just made in.
   *
   * `/play` with no contestId falls through to the app-entry redirect, which picks the
   * soonest contest the player has *not* entered. With two Squares contests live on the
   * same fixture that is reliably the wrong one: having just entered contest A, the
   * player was sent to contest B.
   */
  const handleViewMyEntry = () => {
    onClose();
    const target = contestId ? `/play?contestId=${encodeURIComponent(contestId)}` : '/play';
    navigate(appendLobbyParams(target, searchParams));
  };

  return (
    <SquaresModalShell
      isOpen={isOpen}
      onClose={handleClose}
      testId="square-submitted-modal"
      scrollable
      // Each section below carries its own padding, so clear the shell's defaults.
      cardClassName="p-0 text-left space-y-0"
    >
      {/* Sponsor Logo Section */}
      <div className="pt-8 pb-4 px-4 flex justify-center">
        <div className="relative h-24 w-48 flex items-center justify-center">
          {/* Over Logo Ad Slot (1x1 tracking pixel, positioned just above logo) */}
          {isLogoAdEnabled && overLogoPlacement && (
            <div className="absolute bottom-full w-[1px] h-[1px] overflow-hidden pointer-events-none">
              <AdSlot
                divId={generateAdDivId(adPlacements, 'SQUARES_OVER_LOGO', true, 1)}
                adPlacement={overLogoPlacement}
                placement="squares-over-logo"
              />
            </div>
          )}
          {sponsorLogoUrl && sponsorLogoUrl.trim().length > 0 ? (
            <img src={sponsorLogoUrl} alt="Sponsor Logo" className="h-full w-full object-contain" />
          ) : (
            <img
              src="/DFS_NFL_Wrangler_Squares_Logo_1080x1080%201.svg"
              alt="Wrangler Squares Logo"
              className="h-full w-full object-contain"
            />
          )}
        </div>
      </div>

      {/* Square Submitted Section */}
      <div className="relative pt-2 pb-4 px-4">
        {/* Brush stroke background with text */}
        <div
          className="relative flex justify-center items-center mb-4"
          style={{ minHeight: '76px' }}
        >
          {/* Green checkmark - positioned above and attached to banner */}
          <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 z-20">
            <img src="/Group 5.svg" alt="" className="w-8 h-8" aria-hidden="true" />
          </div>
          {/* Brush stroke background image - full asset behind text */}
          <img
            src="/image 4.svg"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              objectPosition: 'center',
            }}
            aria-hidden="true"
          />

          {/* SQUARE SUBMITTED! text - centered on top of image */}
          <h2
            className="relative z-10 text-white text-center uppercase"
            style={{
              fontFamily: '"Saira Extra Condensed", sans-serif',
              fontWeight: 800,
              fontSize: '20px',
              lineHeight: '24px',
              letterSpacing: '1px',
              padding: '0 20px',
            }}
          >
            SQUARE SUBMITTED!
          </h2>
        </div>

        {/* Matchup Details */}
        <div className="flex items-center justify-center gap-3 mb-4">
          {/* Team 1 Jersey - Image section only */}
          <div className="w-[72px] h-[88px] flex-shrink-0 flex items-center justify-center">
            {team1JerseyUrl && !team1Image.errored ? (
              <>
                {!team1Image.loaded && (
                  <div className="w-full h-full bg-zinc-700/30 rounded animate-pulse" />
                )}
                <img
                  src={team1JerseyUrl}
                  alt="Team 1 jersey"
                  className={`w-full h-full object-contain ${team1Image.loaded ? 'opacity-100' : 'opacity-0 absolute'} transition-opacity duration-200`}
                />
              </>
            ) : (
              <div className="w-full h-full bg-zinc-700/30 border border-zinc-500/40 rounded" />
            )}
          </div>

          {/* VS and Date/Time */}
          <div className="flex flex-col items-center min-w-[60px]">
            <span
              className="text-zinc-100"
              style={{
                fontFamily: '"Saira Extra Condensed", sans-serif',
                fontWeight: 800,
                fontSize: '16px',
                lineHeight: '18px',
              }}
            >
              VS
            </span>
            <span
              className="text-zinc-300 text-center whitespace-pre-line"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: '11px',
                lineHeight: '14px',
              }}
            >
              {gameDate}
              {'\n'}
              {gameTime}
            </span>
          </div>

          {/* Team 2 Jersey - Image section only */}
          <div className="w-[72px] h-[88px] flex-shrink-0 flex items-center justify-center">
            {team2JerseyUrl && !team2Image.errored ? (
              <>
                {!team2Image.loaded && (
                  <div className="w-full h-full bg-zinc-700/30 rounded animate-pulse" />
                )}
                <img
                  src={team2JerseyUrl}
                  alt="Team 2 jersey"
                  className={`w-full h-full object-contain ${team2Image.loaded ? 'opacity-100' : 'opacity-0 absolute'} transition-opacity duration-200`}
                />
              </>
            ) : (
              <div className="w-full h-full bg-zinc-700/30 border border-zinc-500/40 rounded" />
            )}
          </div>
        </div>

        {/* Instructional Text */}
        <p
          className="text-zinc-200 text-center"
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
          }}
        >
          Come back at tip off to check your numbers
        </p>
      </div>

      {/*
       * Post-entry ad — GAM when it fills, otherwise the contest's submit-view promo.
       * Never both: whichever one is showing owns the space.
       */}
      {showPostEntryGam && postEntryPlacement && adPlacements ? (
        /*
         * `px-4` like every other section in the modal.
         *
         * #786 briefly dropped it, on the theory that the shell's `max-w-[340px]` minus
         * `px-4` left the slot 308px, squeezing the 320px creative until its copy reflowed
         * past 50px. Reverted here: shipped to prod as prod-v1.5.182 and the clipping was
         * unchanged, so the 12px was never the cause. Do not re-try it — the defect is in
         * the trafficked creative, not in this wrapper's width (see `FooterAdvert.tsx`).
         */
        <div className="px-4 pb-4">
          <AdSlot
            divId={generateAdDivId(adPlacements, 'SQUARES_POST_ENTRY')}
            adPlacement={postEntryPlacement}
            placement="squares-post-entry"
            /*
             * NO `overflow-hidden` HERE. It is what was clipping the ad.
             *
             * The T-Mobile creative is a third-party DCM tag whose document renders taller
             * than the 320x50 it is served at on iOS WebKit. The anchored footer slot has no
             * `overflow-hidden`, so that excess simply spills and stays visible — which is
             * why the same tag, same device, same release, renders COMPLETE there and cut
             * here. This wrapper and the rules wrapper were the only two carrying
             * `overflow-hidden`, and they were the only two clipping.
             *
             * Six deploys of iOS `min-height` never touched this, because those rules sized
             * the IFRAME while the clip was happening at the WRAPPER.
             *
             * Rounded corners are kept via `[&_iframe]:rounded-lg`, which rounds the frame
             * itself instead of clipping a box around it. Do not "tidy" that back into
             * `overflow-hidden`.
             *
             * `squares-post-entry-advert` is a styling hook, not decoration — `index.css`
             * reaches the GPT iframe through it to set an iOS `min-height` floor. Same
             * arrangement as the footer and rules slots; kept as a real class rather than
             * the `data-test-id` AdSlot already renders, so the selector is obviously
             * load-bearing. That floor has not been shown to change what a device renders.
             */
            className="squares-post-entry-advert rounded-lg [&_iframe]:rounded-lg"
            onRenderEnded={isEmpty => setPostEntryFill(isEmpty ? 'empty' : 'filled')}
          />
        </div>
      ) : (
        <PopUpAdComponent
          imageUrl={
            promoImageUrl && promoImageUrl.trim().length > 0 ? promoImageUrl : '/submit-promo.svg'
          }
          mobileImageUrl={promoImageMobileUrl?.trim() || undefined}
          clickUrl={promoClickUrl}
          altText="Promotional Advertisement"
          slotId="square-submitted-popup-ad"
        />
      )}

      {/* Call to Action Section */}
      <div className="px-4 pb-5 text-center">
        <SquaresPrimaryButton onClick={handleViewMyEntry}>VIEW MY ENTRY</SquaresPrimaryButton>
      </div>
    </SquaresModalShell>
  );
}
